import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { SubletService } from '@org/shared-services';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Sublet } from '@org/interfaces';

@Component({
    selector: 'app-sublet-selection',
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './subletSelection.component.html',
    styleUrl: './subletSelection.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('slideInOut', [
            state('in', style({
                transform: 'translateX(0)'
            })),
            transition(':enter', [
                style({ transform: 'translateX(100%)' }),
                animate('200ms ease-in')
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
            ])
        ])
    ]
})
export class SubletSelectionComponent implements OnInit {

  options: Sublet[] = [];
  glassOptions!: FormArray;
  selectedOptions: Sublet[] = [];
  slideOutVisible = true;
  subletForm!: FormGroup;

  @Output() closeSublet = new EventEmitter<void>();
  
  constructor(private subletService: SubletService, private fb: FormBuilder, private cdr: ChangeDetectorRef){}

  ngOnInit(): void {
    this.subletForm = this.fb.group({
      glassOptions: this.fb.array([])
    });
    this.loadSubletTasks();
   
  }

   // Load sublet tasks from the database
   loadSubletTasks() {
    this.subletService.getSubletTasks().subscribe({
      next: (data: Sublet[]) => {
        this.options = data.map(task => ({
          ...task,
          selected: false,
          subcategories: task.subcategories.map(subcategory => ({
            ...subcategory,
            selected: false,
            isSubcategory: true
          }))
        }));
        this.syncOptionsWithService(); // Sync selected states after loading
        this.initializeGlassOptions();
        this.cdr.markForCheck(); // Manually trigger change detection
      },
      error: (error) => {
        console.error('Error loading sublet tasks:', error);
      }
    });
   }
  
   // Initialize glass options based on the backend data
   initializeGlassOptions() {
    const glassSublet = this.options.find(option => option.name === 'Glass');
    if (glassSublet) {
      this.glassOptions = this.fb.array(glassSublet.subcategories.map(subcategory => this.fb.control(subcategory.selected)));
      this.subletForm.setControl('glassOptions', this.glassOptions);
    }
  }

  // // Synchronize the selected options with the service
  // syncOptionsWithService() {
  //   const selectedOptions = this.subletService.selectedOptionsSubject.value;
  //   this.options.forEach(option => {
  //     option.selected = selectedOptions.some(selected => selected.name === option.name);
  //     option.subcategories.forEach(subcategory => {
  //       subcategory.selected = selectedOptions.some(selected => selected.name === subcategory.name);
  //     });
  //   });
  // }

  // Synchronize the selected options with the service
  syncOptionsWithService() {
    const selectedOptions = this.subletService.selectedOptionsSubject.value;

    this.options.forEach(option => {
      option.selected = selectedOptions.some(selected => selected.name === option.name);
      option.subcategories.forEach(subcategory => {
        subcategory.selected = selectedOptions.some(selected => selected.name === subcategory.name);
      });
    });

    //this.initializeGlassOptions();
  }
  
  // selectOption(option: any) {
  //   option.selected = !option.selected;
  //   if (option.selected) {
  //     this.selectedOptions.push(option);
  //   } else {
  //    // this.selectedOptions = this.selectedOptions.filter(opt => opt !== option);
  //     this.updateSelectedOptions();
  //   }
  //   this.cdr.markForCheck();
  // }

  selectOption(option: Sublet, subcategoryIndex?: number) {
    if (subcategoryIndex !== undefined) {
      option.subcategories[subcategoryIndex].selected = !option.subcategories[subcategoryIndex].selected;
      this.glassOptions.at(subcategoryIndex).setValue(option.subcategories[subcategoryIndex].selected);
    } else {
      option.selected = !option.selected;
    }
    this.updateSelectedOptions();
    this.cdr.markForCheck(); // Manually trigger change detection
  }

  // updateSelectedOptions() {
  //   this.selectedOptions = this.options.filter(option => option.selected);
  // }

  updateSelectedOptions() {
    this.selectedOptions = [];
    this.options.forEach(option => {
      // Remove the special case for Glass
      if (option.selected) {
        this.selectedOptions.push(option);
      }
      option.subcategories.forEach(subcategory => {
        if (subcategory.selected) {
          this.selectedOptions.push({
            ...subcategory,
            isSubcategory: true,
            subcategories: []
          });
        }
      });
    });
  }

  // resetOptions() {
  //   this.options.forEach(option => {
  //     option.selected = false;
  //     option.subcategories.forEach((subcategory: { selected: boolean; }) => subcategory.selected = false);
  //   });
  //   this.updateSelectedOptions();
  //   this.subletService.resetOptions(); // Reset all options
  //   this.glassOptions.reset();
  // }

  resetOptions() {
    this.options.forEach(option => {
      option.selected = false;
      option.subcategories.forEach(subcategory => subcategory.selected = false);
    });
    this.selectedOptions = [];
    this.updateSelectedOptions();
    this.subletService.resetOptions(); // Reset all options
    this.glassOptions.reset();
    this.cdr.markForCheck(); // Manually trigger change detection
  }

  isGlassSubletSelected() {
    return this.options.some(option => option.name === 'Glass' && option.selected);
  }

  get isAnyParentSubletSelected(): boolean {
    return this.options.some(option => !option.isSubcategory && option.selected);
  }

  selectNone() {
    this.subletService.resetOptions(); // Reset all options
    this.closeSlideOut(); // Close the slide-out
    this.syncOptionsWithService(); // Ensure the options are reset visually
  }

  closeSlideOut() {
    this.subletService.closeSublet();
  }

  // submit() {
  //   // Logic to handle the submission of selected options
  //   this.subletService.resetOptions();
  //   this.selectedOptions.forEach(option => {
  //     this.subletService.addOption(option);
  //   });
  //   this.closeSlideOut();
  // }

  // submit() {
  //   // Get selected glass options
  //   const selectedGlassOptions = this.glassOptions.controls
  //     .map((control, i) => control.value ? this.glassOptionNames[i] : null)
  //     .filter(value => value !== null);

  //   // Logic to handle the submission of selected options
  //   this.subletService.resetOptions();
  //   this.selectedOptions.forEach(option => {
  //     this.subletService.addOption(option);
  //   });

  //   // Add selected glass options to the service
  //   selectedGlassOptions.forEach(glassOption => {
  //     this.subletService.addOption({ name: glassOption });
  //   });

  //   this.closeSlideOut();
  // }

  submit() {
    // Get selected glass options
    const selectedGlassOptions = this.glassOptions.controls
      .map((control, i) => control.value ? this.options.find(option => option.name === 'Glass')?.subcategories[i].name : null)
      .filter(value => value !== null);
  
    this.updateSelectedOptions();
    this.subletService.resetOptions();
  
    // Collect all selected options
    const currentSelectedOptions = this.subletService.selectedOptionsSubject.value || [];
    const allSelectedOptions = [...currentSelectedOptions];
    const uniqueNames = new Set(allSelectedOptions.map(option => option.name));
  
    // Helper function to add options uniquely
    const addOptionUniquely = (option: any) => {
      if (option && !uniqueNames.has(option.name)) {
        allSelectedOptions.push(option);
        uniqueNames.add(option.name);
      }
    };
  
    // Add all selected options (including Glass if selected)
    this.selectedOptions.forEach(option => {
      addOptionUniquely(option);
    });
  
    // Update the BehaviorSubject once with the entire array
    this.subletService.selectedOptionsSubject.next(allSelectedOptions);
  
    this.closeSlideOut();
  }

  trackByName(index: number, item: any): string {
    return item.name;
  }

  trackByIndex(index: number): number {
    return index;
  }

}
